import { translateObject } from '@huggg/bar-staff';
import { prop } from 'ramda';
import { urls } from '../config';
import withAxios from '../utils/with-axios';
import { brandMap, hugggMap, productMap, superhugggMap, transactionMap, userMap } from '../translationMaps';
var keyToTranslationMap = {
  brand: brandMap,
  buyer: userMap,
  huggg: hugggMap,
  purchase: productMap,
  receiver: userMap,
  sender: userMap,
  superhuggg: superhugggMap,
  transaction: transactionMap
};

/**
 * Formats the response data by passing each object through the translation map
 *
 * @param {Object} data API response data
 * @returns {Object} Formatted response data
 */
function formatHugggsResponse(response) {
  var translatedHits = response.data.hits.hits.map(function (hit) {
    var translated = Object.keys(keyToTranslationMap).reduce(function (acc, key) {
      var obj = acc._source[key];
      if (!obj) {
        return acc;
      }
      var translationMap = keyToTranslationMap[key];
      if (!translationMap) {
        return acc;
      }
      acc._source[key] = translateObject(translationMap, obj);
      return acc;
    }, hit);
    return translated;
  });
  response.data.hits.hits = translatedHits;
  return response;
}

/**
 * Searches hugggs via the Search Microservice
 *
 * @param {Object} [$axios=axios] Axios instance.
 * @param {Object} body The search body
 * @param {Object} params The search query options
 *
 * @returns {Promise} Unresolved Promise from the API.
 * @throws {Error} If no userId provided
 */
function hugggs(axios, body, params) {
  return axios.post(urls.apiV3('/searches'), body, {
    params: params
  }).then(prop('data')).then(formatHugggsResponse);
}

/**
 * Get a PIT to search with.
 *
 * A PIT is used to paginate through searches and ensures that the results do not change
 * while you do. The dataset is captured at the time the PIT is created and so when using
 * the PIT in search requests the same dataset will be queried for the lifetime of the PIT.
 *
 * This can be used in search.hugggs requests by passing the `pit` in the request body.
 * E.g.
 *
 * const { data: pit } = await search.getPit('hugggs');
 * const searchResponse = await search.hugggs({ query: '...', sort: '...', pit });
 *
 * To paginate using a PIT, rather than using the `from` and `size` fields, you use the PIT and
 * the `searchAfter` field.
 *
 * E.g.
 *
 * const page1 = await search.hugggs({ query: '...', sort: '...', pit });
 * const lastResultOfPage1 = page1.hits.hits.pop();
 *
 * const page2 = await search.hugggs({
 *   query: '...',
 *   sort: '...',
 *   pit,
 *   searchAfter: lastResultOfPage1.sort
 * });
 *
 * Note that the same PIT is used in both search requests. Also note that the second request includes
 * a `searchAfter` field which is the `sort` field of the final result of the previous search - this
 * tells Elasticsearch where to offset the query results.
 *
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} index The index that will be searched against
 *
 * @returns {Promise<Object>} The PIT
 */
function getPit(axios, index) {
  return axios.get(urls.apiV3("/search-indexes/".concat(index, "/point-in-time"))).then(prop('data'));
}

/**
 * Delete a PIT.
 *
 * @param {Object} [$axios=axios] Axios instance.
 * @param {String} index The index that the PIT is for.
 * @param {Object} pit The PIT to delete.
 *
 * @returns {Promise<Object>}
 */
function deletePit(axios, index, pit) {
  return axios.delete(urls.apiV3("/search-indexes/".concat(index, "/point-in-time")), {
    data: {
      pit: pit
    }
  });
}
export default {
  hugggs: withAxios(hugggs),
  getPit: withAxios(getPit),
  deletePit: withAxios(deletePit)
};